<template>
  <Tabs v-model="model" :class="cn('tw-flex tw-flex-col tw-flex-1 tw-min-h-0', props.class)">
    <div
      class="tw-flex tw-items-end tw-gap-1 tw-shrink-0 tw-overflow-x-auto tw-border-b tw-border-surface-200 dark:tw-border-neutral-800"
    >
      <TabsList class="tw-h-auto tw-flex tw-items-end tw-gap-1 tw-bg-transparent tw-p-0">
        <TabsTrigger
          v-for="tab in tabs"
          :key="tab.value"
          :value="tab.value"
          class="tw-group tw-relative tw-inline-flex tw-h-auto tw-max-w-[220px] tw-items-center tw-gap-2 tw-rounded-none tw-border-0 tw-bg-transparent tw-px-3.5 tw-py-3 tw-text-[0.9rem] tw-font-medium tw-tracking-tight tw-text-surface-500 tw-shadow-none tw-transition-colors hover:tw-text-secondary-700 data-[state=active]:tw-bg-transparent data-[state=active]:tw-text-secondary-900 data-[state=active]:tw-font-semibold data-[state=active]:tw-shadow-none dark:tw-text-neutral-400 dark:hover:tw-text-neutral-200 dark:data-[state=active]:tw-text-neutral-50 after:tw-absolute after:tw-bottom-[-1px] after:tw-left-2 after:tw-right-2 after:tw-h-[2.5px] after:tw-rounded-full after:tw-bg-transparent after:tw-transition-colors data-[state=active]:after:tw-bg-secondary-700 dark:data-[state=active]:after:tw-bg-primary-400"
        >
          <component
            v-if="tab.icon"
            :is="tab.icon"
            class="tw-h-4 tw-w-4 tw-shrink-0 tw-text-surface-400 group-data-[state=active]:tw-text-secondary-700 dark:group-data-[state=active]:tw-text-primary-400"
          />
          <span class="tw-truncate">{{ tab.label }}</span>
          <button
            v-if="tab.closable"
            type="button"
            class="tw-ml-0.5 tw-rounded-md tw-p-0.5 tw-text-surface-400 tw-opacity-0 tw-transition-opacity hover:tw-bg-surface-200 hover:tw-text-red-600 group-hover:tw-opacity-100 data-[state=active]:tw-opacity-100 dark:hover:tw-bg-neutral-800"
            @click.stop="emit('close', tab.value)"
          >
            <X class="tw-h-3 tw-w-3" />
          </button>
        </TabsTrigger>
      </TabsList>

      <button
        v-if="showAdd"
        type="button"
        class="tw-mb-1.5 tw-ml-1 tw-inline-flex tw-h-8 tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-md tw-px-2.5 tw-text-sm tw-font-medium tw-text-surface-500 tw-transition-colors hover:tw-bg-surface-100 hover:tw-text-secondary-700 dark:hover:tw-bg-neutral-800"
        @click="emit('add')"
      >
        <Plus class="tw-h-4 tw-w-4" />
        <span v-if="addLabel">{{ addLabel }}</span>
      </button>

      <slot name="trailing" />
    </div>

    <TabsContent
      v-for="tab in tabs"
      :key="tab.value"
      :value="tab.value"
      class="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-mt-0 tw-pt-3 data-[state=inactive]:tw-hidden"
    >
      <slot :name="tab.value" />
    </TabsContent>
  </Tabs>
</template>

<script setup lang="ts">
import type { Component, HTMLAttributes } from 'vue';
import { Plus, X } from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export interface AlascoDocumentTab {
  value: string;
  label: string;
  icon?: Component;
  closable?: boolean;
}

interface Props {
  tabs: AlascoDocumentTab[];
  showAdd?: boolean;
  addLabel?: string;
  class?: HTMLAttributes['class'];
}

const props = withDefaults(defineProps<Props>(), {
  showAdd: false,
  addLabel: '',
});

const model = defineModel<string>();

const emit = defineEmits<{
  add: [];
  close: [value: string];
}>();
</script>
